import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { RefreshCw, RotateCw, Search } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  sent: "default",
  retrying: "secondary",
  gave_up: "destructive",
  invalid_phone: "destructive",
  dry_run: "outline",
  queued: "secondary",
};

const TEMPLATES = ["otp", "order_confirmation", "tracking"];

export default function AdminSmsLogs() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("all");
  const [template, setTemplate] = useState("all");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status !== "all") params.set("status", status);
      if (template !== "all") params.set("template", template);
      if (phone.trim()) params.set("phone", phone.trim());
      const r = await api.get(`/admin/sms-logs?${params}`);
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load SMS logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, template]);

  const retry = async (log_id) => {
    try {
      await api.post(`/admin/sms-logs/${log_id}/retry`);
      toast.success("Queued for immediate retry");
      setTimeout(load, 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";
  const short = (s, n=48) => s && s.length > n ? s.slice(0, n) + "…" : (s || "—");

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-outfit font-bold" data-testid="sms-logs-title">SMS Delivery Log</h1>
          <div className="text-sm text-stone-500">MDT gateway · DLYBNS sender</div>
        </div>
        <button onClick={load} data-testid="sms-logs-refresh"
                className="flex items-center gap-2 bg-[#3B2F2F] text-white rounded-md px-4 py-2 text-sm">
          <RefreshCw className={`w-4 h-4 ${loading?"animate-spin":""}`}/> Refresh
        </button>
      </div>

      {/* Bucket cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          ["sent","Sent","#2E4A35"],
          ["retrying","Retrying","#B8860B"],
          ["gave_up","Failed","#B00020"],
          ["invalid_phone","Bad phone","#8B7355"],
          ["dry_run","Dry-run","#57534E"],
        ].map(([k,label,color])=>(
          <div key={k} data-testid={`sms-count-${k}`}
               className="bg-white border border-stone-200 rounded-lg px-4 py-3">
            <div className="text-xs uppercase tracking-widest text-stone-500">{label}</div>
            <div className="text-2xl font-outfit font-bold" style={{color}}>
              {counts[k] || 0}
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 bg-white" data-testid="sms-filter-status"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {["sent","retrying","gave_up","invalid_phone","dry_run","queued"].map(s=>
              <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={template} onValueChange={setTemplate}>
          <SelectTrigger className="w-52 bg-white" data-testid="sms-filter-template"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All templates</SelectItem>
            {TEMPLATES.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-md px-3">
          <Search className="w-4 h-4 text-stone-400"/>
          <Input value={phone} onChange={e=>setPhone(e.target.value)}
                 placeholder="Filter by phone (10 digits)"
                 data-testid="sms-filter-phone"
                 onKeyDown={e=>e.key==='Enter'&&load()}
                 className="border-0 focus-visible:ring-0 h-9 w-56"/>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>When</TableHead>
            <TableHead>Template</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Attempts</TableHead>
            <TableHead>MDT Message ID</TableHead>
            <TableHead>Message</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-stone-500 py-8">
                No SMS logs match these filters yet.
              </TableCell></TableRow>
            )}
            {items.map(o => (
              <TableRow key={o.log_id} data-testid={`sms-row-${o.log_id}`}>
                <TableCell className="text-xs whitespace-nowrap">{fmt(o.created_at)}</TableCell>
                <TableCell className="text-xs uppercase tracking-wider">{o.template}</TableCell>
                <TableCell className="font-mono text-xs">{o.phone || o.phone_raw}</TableCell>
                <TableCell>
                  <Badge variant={STATUS_STYLE[o.status] || "outline"}>{o.status}</Badge>
                </TableCell>
                <TableCell className="text-xs">{o.attempts}/3</TableCell>
                <TableCell className="font-mono text-[11px] break-all max-w-[10rem]">{o.messageid || "—"}</TableCell>
                <TableCell className="text-xs text-stone-600 max-w-md" title={o.message}>{short(o.message, 60)}</TableCell>
                <TableCell className="text-right">
                  {(o.status === "gave_up" || o.status === "retrying") && (
                    <button onClick={()=>retry(o.log_id)} data-testid={`sms-retry-${o.log_id}`}
                            className="inline-flex items-center gap-1 text-xs bg-[#3B2F2F] text-white rounded px-2 py-1 hover:bg-black">
                      <RotateCw className="w-3 h-3"/> Retry now
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
